import { useLingui } from '@lingui/react/macro'
import { usePlotBounds } from 'features/timeSeries/components/chartOverlays/usePlotBounds'
import { useCursorData } from 'features/timeSeries/CursorDataContext'
import { getLabelGroup } from 'features/timeSeries/labels/labelGroups'
import type { ProcessDataLabel } from 'features/timeSeries/labels/processDataLabelsService'
import React, { useLayoutEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import uPlot from 'uplot'

import {
  getLabelFlagPlacement,
  getTitleLaneCapacity,
  LabelRange,
  packLabelTitleLanes,
  projectLabelSpan,
  TITLE_FLAG_CHROME_PX,
  TITLE_FLAG_TIP_WIDTH,
  TITLE_LANE_STRIDE,
  TITLE_TOP_INSET,
} from './labelGeometry'
import type { LabelDraft, LabelEdgeTarget } from './useLabelTool'

/* ─── Styled components ─────────────────────────────────────────────────── */

interface LabelColors {
  accent: string
  fill: string
  fillFocused: string
}

/**
 * Amber, against the ruler's blue (`CHART_COLORS.CURSOR_ACCENT`): the two
 * markup tools draw the same full-height band, so hue is what tells a label
 * region apart from a measurement region at a glance. Ungrouped labels and the
 * in-progress draft draw in it.
 */
const AMBER: LabelColors = {
  accent: '#e8a33d',
  fill: 'rgba(232, 163, 61, 0.1)',
  fillFocused: 'rgba(232, 163, 61, 0.2)',
}

function resolveLabelColors(label: ProcessDataLabel): LabelColors {
  const group = getLabelGroup(label.groupId)
  if (!group) return AMBER
  return {
    accent: group.accent,
    fill: group.fill,
    fillFocused: group.fillFocused,
  }
}

/** Text on a flag filled with a group accent; all seven clear 5.8:1 against it. */
const FLAG_TEXT = '#0d0d0d'

/**
 * A promoted flag: brighter, lifted off the chart, and ringed in white. The
 * ring is four offset drop-shadows because `border` and `outline` are both cut
 * by the `clip-path` that makes the point. It has to sit on the flag's WRAPPER:
 * an element's own filter is painted before its clip-path, so a ring applied
 * here would be clipped away with everything else outside the pennant.
 */
const FLAG_EMPHASIS = `brightness(1.14)
    drop-shadow(1.5px 0 0 #fff) drop-shadow(-1.5px 0 0 #fff)
    drop-shadow(0 1.5px 0 #fff) drop-shadow(0 -1.5px 0 #fff)
    drop-shadow(0 2px 4px rgba(0, 0, 0, 0.9))`

const TITLE_FONT = "600 10px 'Manrope', sans-serif"
const DRAFT_TITLE = 'New label'

const OverlayRoot = styled.div<{ $isActive: boolean; $resizing: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 14;
  cursor: ${({ $isActive, $resizing }) =>
    !$isActive ? 'default' : $resizing ? 'ew-resize' : 'crosshair'};
  pointer-events: ${({ $isActive }) => ($isActive ? 'all' : 'none')};
  user-select: none;
`

const LabelBand = styled.div<{
  $focused: boolean
  $isDraft: boolean
  $colors: LabelColors
}>`
  position: absolute;
  background: ${({ $colors, $focused }) =>
    $focused ? $colors.fillFocused : $colors.fill};
  border-left: 1px ${({ $isDraft }) => ($isDraft ? 'dashed' : 'solid')}
    ${({ $colors }) => $colors.accent};
  border-right: 1px ${({ $isDraft }) => ($isDraft ? 'dashed' : 'solid')}
    ${({ $colors }) => $colors.accent};
  pointer-events: none;
`

/**
 * Holds every flag and clips them to the plot area. One layer over the whole
 * plot rather than a box per flag: a flag is free to run past its own band,
 * and only the chart's edge may cut it.
 */
const TitleLayer = styled.div`
  position: absolute;
  overflow: hidden;
  pointer-events: none;
  z-index: 16;
`

const FlagWrap = styled.div<{ $emphasized: boolean }>`
  position: absolute;
  pointer-events: none;
  filter: ${({ $emphasized }) => ($emphasized ? FLAG_EMPHASIS : 'none')};
`

const TitleFlag = styled.button<{ $colors: LabelColors }>`
  display: block;
  pointer-events: all;
  background: ${({ $colors }) => $colors.accent};
  border: none;
  margin: 0;
  padding: 1px ${7 + TITLE_FLAG_TIP_WIDTH}px 1px 7px;
  font-family: 'Manrope', sans-serif;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.5;
  color: ${FLAG_TEXT};
  white-space: nowrap;
  cursor: pointer;
  clip-path: polygon(
    0 0,
    calc(100% - ${TITLE_FLAG_TIP_WIDTH}px) 0,
    100% 50%,
    calc(100% - ${TITLE_FLAG_TIP_WIDTH}px) 100%,
    0 100%
  );
`

const ResizeHandle = styled.div`
  position: absolute;
  width: 10px;
  cursor: ew-resize;
  z-index: 25;
  pointer-events: all;
`

/* ─── Title measurement ──────────────────────────────────────────────────── */

const textWidthCache = new Map<string, number>()
let measureContext: CanvasRenderingContext2D | null | undefined

/**
 * Rendered width of a title flag. Flags are variable-width text that is never
 * truncated, so the packing needs the real width rather than a per-character
 * estimate; the cache keeps that off the render path, which reruns on every
 * pan frame.
 */
function measureFlagWidth(text: string, font = TITLE_FONT): number {
  const key = `${font} ${text}`
  const cached = textWidthCache.get(key)
  if (cached !== undefined) return cached

  if (measureContext === undefined) {
    measureContext = document.createElement('canvas').getContext('2d')
  }
  if (!measureContext) return text.length * 6 + TITLE_FLAG_CHROME_PX

  measureContext.font = font
  const width =
    Math.ceil(measureContext.measureText(text).width) + TITLE_FLAG_CHROME_PX
  textWidthCache.set(key, width)
  return width
}

/* ─── Component ──────────────────────────────────────────────────────────── */

interface TitleLayout {
  id: string
  title: string
  colors: LabelColors
  focused: boolean
  left: number
  width: number
  onClick: () => void
}

interface LabelOverlayProps {
  uPlotRef: React.RefObject<uPlot | null>
  isActive: boolean
  /** See `shouldRenderPersistedOverlays` — false while there is nothing to anchor to. */
  showPersisted: boolean
  labels: ProcessDataLabel[]
  /** Applies the live edge drag, so a resize follows the mouse before it commits. */
  getRenderedRange: (label: ProcessDataLabel) => LabelRange
  draft: LabelDraft | null
  editingLabelId: string | null
  resizingEdge: 'start' | 'end' | null
  onEditLabel: (labelId: string) => void
  handleEdgeMouseDown: (
    target: LabelEdgeTarget,
    edge: 'start' | 'end',
    e: React.MouseEvent
  ) => void
  onMouseMove: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseUp: (e: React.MouseEvent<HTMLDivElement>) => void
  onMouseLeave: () => void
}

export const LabelOverlay: React.FC<LabelOverlayProps> = ({
  uPlotRef,
  isActive,
  showPersisted,
  labels,
  getRenderedRange,
  draft,
  editingLabelId,
  resizingEdge,
  onEditLabel,
  handleEdgeMouseDown,
  onMouseMove,
  onMouseDown,
  onMouseUp,
  onMouseLeave,
}) => {
  const { t } = useLingui()

  const rootRef = useRef<HTMLDivElement | null>(null)
  const plotBounds = usePlotBounds(uPlotRef, rootRef)
  const { hoveredLabelId } = useCursorData()
  const [hoveredTitleId, setHoveredTitleId] = useState<string | null>(null)

  // Regions are projected from their time range on every render, but a uPlot
  // zoom/pan can move the x-scale without any React state changing. Re-render
  // on `setScale` so the projection and the title packing below re-run against
  // the new scale.
  const [, setLayoutVersion] = useState(0)
  useLayoutEffect(() => {
    const u = uPlotRef.current
    if (!u) return

    const handler = (_u: uPlot, key: string) => {
      if (key !== 'x') return
      setLayoutVersion((version) => version + 1)
    }

    u.hooks.setScale = u.hooks.setScale ?? []
    u.hooks.setScale.push(handler)

    return () => {
      if (u.hooks.setScale) {
        u.hooks.setScale = u.hooks.setScale.filter((hook) => hook !== handler)
      }
    }
  }, [uPlotRef])

  // Widths measured before the title face loads are the fallback face's, and
  // the packing would keep using them for the life of the page.
  useLayoutEffect(() => {
    let cancelled = false
    void document.fonts.ready.then(() => {
      if (cancelled) return
      textWidthCache.clear()
      setLayoutVersion((version) => version + 1)
    })
    return () => {
      cancelled = true
    }
  }, [])

  const u = uPlotRef.current
  const valToPos =
    u && plotBounds
      ? (value: number, scale: string) => u.valToPos(value, scale)
      : null

  const plotTop = plotBounds?.overTop ?? 0
  const plotBottom = plotBounds
    ? `calc(100% - ${plotBounds.overBottom}px)`
    : '0'
  const layerLeft = plotBounds?.overLeft ?? 0

  const renderBand = (
    key: string,
    startPx: number,
    endPx: number,
    options: {
      focused: boolean
      isDraft: boolean
      colors: LabelColors
      target: LabelEdgeTarget
      resizable: boolean
    }
  ) => (
    <React.Fragment key={key}>
      <LabelBand
        data-testid={options.isDraft ? 'label-draft-band' : 'label-band'}
        data-label-id={options.isDraft ? undefined : key}
        $focused={options.focused}
        $isDraft={options.isDraft}
        $colors={options.colors}
        style={{
          left: startPx,
          width: Math.max(0, endPx - startPx),
          top: plotTop,
          bottom: plotBottom,
        }}
      />
      {options.resizable && (
        <>
          <ResizeHandle
            style={{ left: startPx - 5, top: plotTop, bottom: plotBottom }}
            onMouseDown={(event) =>
              handleEdgeMouseDown(options.target, 'start', event)
            }
          />
          <ResizeHandle
            style={{ left: endPx - 5, top: plotTop, bottom: plotBottom }}
            onMouseDown={(event) =>
              handleEdgeMouseDown(options.target, 'end', event)
            }
          />
        </>
      )}
    </React.Fragment>
  )

  const bands: React.ReactNode[] = []
  const titles: TitleLayout[] = []

  if (showPersisted && plotBounds) {
    const plot = {
      plotLeft: plotBounds.overLeft,
      plotRight: plotBounds.overRight,
    }

    for (const label of labels) {
      const { startPx, endPx, horizontallyVisible } = projectLabelSpan(
        getRenderedRange(label),
        valToPos,
        plotBounds
      )
      if (!horizontallyVisible) continue

      const colors = resolveLabelColors(label)
      const focused =
        editingLabelId === label.id ||
        hoveredLabelId === label.id ||
        hoveredTitleId === label.id

      bands.push(
        renderBand(label.id, startPx, endPx, {
          focused,
          isDraft: false,
          colors,
          target: { kind: 'label', id: label.id },
          resizable: isActive,
        })
      )

      const title = label.name || t`Untitled label`
      const width = measureFlagWidth(title)
      // `keepInView` costs the exact anchor only for a flag the plot's right
      // edge would otherwise cut in half, which would read as the truncation
      // the flag exists to avoid.
      const flag = getLabelFlagPlacement({
        startPx,
        endPx,
        width,
        keepInView: true,
        ...plot,
      })
      if (!flag.visible) continue

      titles.push({
        id: label.id,
        title,
        colors,
        focused,
        left: flag.left,
        width,
        onClick: () => onEditLabel(label.id),
      })
    }
  }

  const lanes = packLabelTitleLanes(
    titles.map(({ id, left, width }) => ({ id, left, width })),
    {
      maxLanes: getTitleLaneCapacity({
        plotHeight: plotBounds ? plotBounds.overBottom - plotBounds.overTop : 0,
      }),
    }
  )

  const renderTitle = (layout: TitleLayout) => {
    const lane = lanes.get(layout.id) ?? 0
    // Promotion only lifts the flag over its neighbours: nothing is hidden or
    // shortened at rest, so there is nothing to expand.
    const promoted =
      hoveredTitleId === layout.id || hoveredLabelId === layout.id

    return (
      <FlagWrap
        key={layout.id}
        $emphasized={promoted || layout.focused}
        style={{
          left: layout.left - layerLeft,
          top: TITLE_TOP_INSET + lane * TITLE_LANE_STRIDE,
          zIndex: promoted ? 2 : 1,
        }}>
        <TitleFlag
          type='button'
          data-testid='label-title-flag'
          data-label-id={layout.id}
          $colors={layout.colors}
          onMouseEnter={() => setHoveredTitleId(layout.id)}
          onMouseLeave={() => setHoveredTitleId(null)}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={layout.onClick}>
          {layout.title}
        </TitleFlag>
      </FlagWrap>
    )
  }

  const draftSpan = draft
    ? projectLabelSpan(
        { startMs: draft.startMs, endMs: draft.endMs },
        valToPos,
        plotBounds
      )
    : null
  const draftFlag =
    draftSpan?.horizontallyVisible && plotBounds
      ? getLabelFlagPlacement({
          startPx: draftSpan.startPx,
          endPx: draftSpan.endPx,
          width: measureFlagWidth(DRAFT_TITLE),
          plotLeft: plotBounds.overLeft,
          plotRight: plotBounds.overRight,
          keepInView: true,
        })
      : null

  return (
    <OverlayRoot
      ref={rootRef}
      data-testid='label-overlay'
      $isActive={isActive}
      $resizing={resizingEdge !== null}
      onMouseMove={isActive ? onMouseMove : undefined}
      onMouseDown={isActive ? onMouseDown : undefined}
      onMouseUp={isActive ? onMouseUp : undefined}
      onMouseLeave={isActive ? onMouseLeave : undefined}>
      {bands}

      {draft &&
        draftSpan?.horizontallyVisible &&
        renderBand('label-draft', draftSpan.startPx, draftSpan.endPx, {
          focused: true,
          isDraft: true,
          colors: AMBER,
          target: { kind: 'draft' },
          resizable: draft.isComplete,
        })}

      {plotBounds && (
        <TitleLayer
          style={{
            left: layerLeft,
            top: plotTop,
            width: Math.max(plotBounds.overRight - layerLeft, 0),
            height: Math.max(plotBounds.overBottom - plotTop, 0),
          }}>
          {titles.map(renderTitle)}
          {draftFlag?.visible && (
            <FlagWrap
              $emphasized
              style={{
                left: draftFlag.left - layerLeft,
                top: TITLE_TOP_INSET,
                zIndex: 3,
              }}>
              <TitleFlag type='button' $colors={AMBER}>
                {DRAFT_TITLE}
              </TitleFlag>
            </FlagWrap>
          )}
        </TitleLayer>
      )}
    </OverlayRoot>
  )
}
